// noinspection JSUnusedGlobalSymbols

import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import {useParams} from "react-router-dom";
import {MarkProductForCacheSectionNames, ProductTechnicalPropertyTypeIds} from "../../../../../../../core/constants/enums";
import Utils, {createUUId, deepEqual, numComparator, stringComparator} from "../../../../../../../core/services/utils/utils";
import Button from "../../../../../../components/base/button/button";
import classnames from "classnames";
import {Add, DeleteOutlined, EditOutlined} from "@mui/icons-material";
import {Autocomplete, Box, CircularProgress, createFilterOptions, TextField, Typography, Unstable_Grid2 as Grid} from "@mui/material";
import SaveOrRollback from "../../../../../../components/app-specific/save-or-rollback";
import IconButton from "../../../../../../components/base/button/icon_button";
import {setReduxDialogState} from "../../../../../../redux/slices/dialogs/actions";
import ReduxStoreRoutes from "../../../../../../../core/models/static/routes/redux-store-routes";
import {useDispatch} from "react-redux";
import Chips, {ChipsIconPosition, ChipsTypes} from "../../../../../../components/base/chips/chips";
import axios from "axios";
import match from "autosuggest-highlight/match";
import parse from "autosuggest-highlight/parse";
import UpsertProductTechnicalPropertyDialog from "../../../../../../components/dialogs/upsert-product-technical-property";
import ManageProductTechnicalPropertiesDialog from "../../../../../../components/dialogs/manage-product-technical-properties";
import Api from "../../../../../../../core/services/api/api";
import ToggleButtons from "../../../../../../components/base/button/toggle_buttons";
import {DatePicker} from "@mui/x-date-pickers";
import DateUtils from "../../../../../../../core/services/date-utils";
import useIndustry from "../../../../../../hooks/use-industry";
import ColorTextField from "../../../../../../components/text-fields/color-field";


const initialAddPropertiesDialogState = {
    open: false,
    industryId: null,
    industryTitle: null,
    productId: null,
    existingPropertyIds: [],
    onAdd: () => void 0,
}

const initialUpdatePropertyDialogState = {
    open: false,
    property: null,
}

const ProductTechnicalPropertiesSection = ({loading: loadingProps, product, setIsCacheNeeded}) => {
    const params = useParams();
    const [loadingData, setLoadingData] = useState(true);
    const [properties, setProperties] = useState([]);
    const [removingIds, setRemovingIds] = useState([]);
    const [addPropertiesDialogState, setAddPropertiesDialogState] = useState(initialAddPropertiesDialogState);
    const [updatePropertyDialogState, setUpdatePropertyDialogState] = useState(initialUpdatePropertyDialogState);
    const [propertyOrders, setPropertyOrders] = useState([])
    const {industries} = useIndustry({noCategories: true})
    const isMounted = useIsMounted();

    const loading = useMemo(() => loadingProps || loadingData, [loadingProps, loadingData])
    const productId = useMemo(() => Number(params.id), [params.id])

    /**
     * Fetches both the technical properties of the current product and its filled values.
     *
     * * merges them into a single list before storing it in the state.
     * @type {(function(): Promise<void>)}
     */
    const getProductTechnicalProperties = useCallback(async () => {
        setLoadingData(true);
        const allProperties = (await Api.getEligibleIndustryPropertiesByPartIds({
            partIds: [productId]
        }))?.data ?? [];
        const response = await Api.listPartProperties(productId)
        if (!isMounted())
            return;
        setLoadingData(false);
        if (response?.resultFlag) {
            const properties = {};
            for (const connectedProperty of response.data) {
                if (properties[connectedProperty.property.id]) {
                    properties[connectedProperty.property.id].selectedOptions.push({
                        id: connectedProperty.id,
                        optionId: connectedProperty.industryPropertyValueId,
                        title: connectedProperty.value
                    });
                } else {
                    const fullProperty = allProperties.find(e => e.id === connectedProperty.property.id);
                    if (!fullProperty)
                        return;
                    properties[fullProperty.id] = {
                        id: fullProperty.id,
                        title: fullProperty.title,
                        typeId: fullProperty.industryPropertyTypeId,
                        hasPredefinedValues: fullProperty.config?.hasPreDefinedValues ?? false,
                        isMultiSelect: fullProperty.config?.isMultiSelect ?? false,
                        isDefault: fullProperty.isDefault,
                        value: connectedProperty.value,
                        industryPropertyValueId: connectedProperty.industryPropertyValueId,
                        partIndustryPropertyId: connectedProperty.id,
                        selectedOptions: (connectedProperty.industryPropertyValueId ? [{
                            id: connectedProperty.id,
                            optionId: connectedProperty.industryPropertyValueId,
                            title: connectedProperty.value
                        }] : []),
                    };
                }
            }
            const res = Object.values(properties);
            res.sort((a, b) => {
                if (!a.orderIndex && !b.orderIndex)
                    return stringComparator(a.title, b.title);
                return numComparator(a.orderIndex, b.orderIndex);
            });
            setProperties(res);
        }
    }, [isMounted, productId])

    /**
     * With each change in id of the url params:
     * - fetches the technical info properties of the product from the server.
     */
    useEffect(() => {
        void getProductTechnicalProperties();
    }, [getProductTechnicalProperties])

    /**
     * Fetches the property orders of the industry.
     * @type {(function(*): Promise<void>)}
     */
    const fetchPropertyOrders = useCallback(async (industryId, cancelToken) => {
        const response = await Api.getIndustryPropertyExtraConfigs(industryId, cancelToken);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setPropertyOrders(response?.data?.propertiesOrders ?? []);
        }
    }, [isMounted])

    /**
     * When the industry changes, fetch the property orders.
     */
    useEffect(() => {
        if (!product?.industryId)
            return;
        const cancelTokenSource = axios.CancelToken.source();
        void fetchPropertyOrders(product?.industryId, cancelTokenSource.token);
        return () => cancelTokenSource.cancel();
    }, [product?.industryId, fetchPropertyOrders])

    /**
     * Updates the value of a property of the product.
     * @type {(function(*, {value: *, optionIds: *}): Promise<void>)}
     */
    const updatePropertyValue = useCallback(async (propertyId, {value, options}) => {
        const property = properties.find(e => e.id === propertyId);
        if (Array.isArray(options) && property.isMultiSelect) {
            const newOptionIds = options.map(e => e.optionId);
            const toBeRemoved = property.selectedOptions.filter(e => !newOptionIds.includes(e.optionId));
            const toBeAdded = newOptionIds.filter(e => !property.selectedOptions.map(e => e.optionId).includes(e)).map(e => options.find(o => o.optionId === e));
            for (const option of toBeRemoved) {
                const response = await Api.removePropertyValueFromPart(option.id);
                if (!isMounted())
                    return;
                if (response?.resultFlag) {
                    Api.markPartsForCache({
                        partIds: [productId],
                        sectionNames: [MarkProductForCacheSectionNames.technical]
                    })
                        .then(response => {
                            if (response?.resultFlag) {
                                setIsCacheNeeded?.(true)
                            }
                        });
                    setProperties(prevState => prevState.map(property => {
                        if (property.id === propertyId) {
                            return {
                                ...property,
                                selectedOptions: property.selectedOptions.filter(e => e.id !== option.id),
                            };
                        }
                        return property;
                    }));
                }
            }
            for (const option of toBeAdded) {
                const forApi = {
                    partId: productId,
                    industryPropertyId: property.id,
                    value: option.title,
                    industryPropertyValueId: option.optionId,
                }
                const response = await Api.addPartIndustryProperty(forApi)
                if (!isMounted())
                    return;
                if (response.resultFlag) {

                    Api.markPartsForCache({
                        partIds: [productId],
                        sectionNames: [MarkProductForCacheSectionNames.technical]
                    })
                        .then(response => {
                            if (response?.resultFlag) {
                                setIsCacheNeeded?.(true)
                            }
                        });
                    setProperties(prevState => prevState.map(property => {
                        if (property.id === propertyId) {
                            const withoutPending = property.selectedOptions.filter(e => e.optionId !== option.optionId);
                            return {
                                ...property,
                                selectedOptions: [...withoutPending, {
                                    id: response.data.id,
                                    optionId: option.optionId,
                                    title: option.title,
                                }],
                            };
                        }
                        return property;
                    }))
                }
            }
        } else if (options.length === 1) {
            const forApi = {
                partIndustryPropertyId: options[0].id,
                industryPropertyValueId: options[0].optionId,
                value: options[0].title,
            };
            const response = await Api.setPartPropertyValue(forApi);
            if (!isMounted())
                return;
            if (response.resultFlag) {
                Api.markPartsForCache({partIds: [productId], sectionNames: [MarkProductForCacheSectionNames.technical]})
                    .then(response => {
                        if (response?.resultFlag) {
                            setIsCacheNeeded?.(true)
                        }
                    });
                setProperties(prevState => prevState.map(property => {
                    if (property.id === propertyId) {
                        return {
                            ...property,
                            value: value ?? "",
                            selectedOptions: [{
                                id: options[0].id,
                                optionId: options[0].optionId,
                                title: options[0].title,
                            }],
                        };
                    }
                    return property;
                }));
            }
        } else {
            let parsedValue;
            switch (property.typeId) {
                case ProductTechnicalPropertyTypeIds.number:
                case ProductTechnicalPropertyTypeIds.decimal:
                case ProductTechnicalPropertyTypeIds.money:
                    parsedValue = Number(value);
                    break;
                case ProductTechnicalPropertyTypeIds.fraction:
                    parsedValue = Utils.technicalPropertyFractionToDecimal(value);
                    break;
                case ProductTechnicalPropertyTypeIds.date:
                    parsedValue = value?.isValid() ? DateUtils.UTCToServer(value).format(DateUtils.withoutOffsetFormat) : null;
                    break;
                case ProductTechnicalPropertyTypeIds.boolean:
                    parsedValue = value === 'true';
                    break;
                default:
                    parsedValue = value ?? ""
            }
            const forApi = {
                partIndustryPropertyId: property.partIndustryPropertyId,
                industryPropertyValueId: property.industryPropertyValueId,
                value: parsedValue,
            };
            const response = await Api.setPartPropertyValue(forApi);
            if (!isMounted())
                return;
            if (response.resultFlag) {
                Api.markPartsForCache({partIds: [productId], sectionNames: [MarkProductForCacheSectionNames.technical]})
                    .then(response => {
                        if (response?.resultFlag) {
                            setIsCacheNeeded?.(true)
                        }
                    });
                setProperties(prevState => prevState.map(property => {
                    if (property.id === propertyId) {
                        return {
                            ...property,
                            value: parsedValue?.toString(),
                            selectedOptions: [],
                        };
                    }
                    return property;
                }));
            }
        }
    }, [isMounted, productId, properties, setIsCacheNeeded])

    /**
     * Removes a property from the product.
     * @type {(function(*): Promise<boolean>)}
     */
    const removePropertyFromProduct = useCallback(async (propertyId) => {
        setRemovingIds(prevState => [...prevState, propertyId]);
        const response = await Api.clearPropertyFromPart(propertyId, productId);
        if (!isMounted())
            return false;
        setRemovingIds(prevState => prevState.filter(id => id !== propertyId));
        if (response.resultFlag) {

            Api.markPartsForCache({partIds: [productId], sectionNames: [MarkProductForCacheSectionNames.technical]})
                .then(response => {
                    if (response?.resultFlag) {
                        setIsCacheNeeded?.(true)
                    }
                });
            setProperties(prevState => prevState.filter(property => property.id !== propertyId));
        }
        return response.resultFlag;
    }, [isMounted, productId, setIsCacheNeeded])

    /**
     * Adds properties to the product.
     * @type {(function(*): void)}
     */
    const onPropertiesAdded = useCallback((properties) => {
        setProperties(prevState => {
            const newState = [
                ...prevState,
                ...properties.map(e => ({
                    id: e.id,
                    title: e.title,
                    typeId: e.typeId,
                    hasPredefinedValues: e.hasPredefinedValues,
                    isDefault: e.isDefault,
                    isMultiSelect: e.isMultiSelect,
                    value: null,
                    partIndustryPropertyId: e.partIndustryPropertyId,
                    selectedOptions: [],
                }))
            ];
            newState.sort((a, b) => stringComparator(a.title, b.title));
            return newState;
        });
    }, [])

    /**
     * Opens the dialog to add new properties to the product.
     * @type {(function(): void)}
     */
    const addNewProperties = useCallback(() => {
        if (!product?.id)
            return;
        setAddPropertiesDialogState({
            open: true,
            industryId: product?.industryId,
            industryTitle: industries.find(e => e.id === product?.industryId)?.title,
            productId: productId,
            existingPropertyIds: properties.map(e => e.id),
            onAdd: onPropertiesAdded,
        });
    }, [industries, onPropertiesAdded, product?.id, product?.industryId, productId, properties])

    /**
     * Opens the dialog to update the value of a property.
     * @type {(function(*): void)|*}
     */
    const updateProperty = useCallback((property) => {
        setUpdatePropertyDialogState({
            open: true,
            property: property,
            onUpsert: (property) => setProperties(prevState => prevState.map(e => e.id === property.id ? {...e, ...property} : e)),
            onClose: () => setUpdatePropertyDialogState(initialUpdatePropertyDialogState),
        });
    }, [])

    const visibleProperties = useMemo(() => {
        const ordered = [];
        for (const id of propertyOrders) {
            const found = properties.find((item) => item.id === id);
            if (found)
                ordered.push(found);
        }
        const rest = properties.filter((item) => !ordered.includes(item));
        rest.sort((a, b) => a.title.localeCompare(b.title));
        ordered.push(...rest);
        return ordered;
    }, [properties, propertyOrders])
    return (
        <>
            <div className={'w-100 part-info-paper pb-4'}>
                <div className={'d-flex justify-content-between align-items-center mb-5 mx-3 mt-3'}>
                    <Typography variant={'body1'} fontWeight={600}>
                        Technical Properties
                    </Typography>
                    <Button
                        className={classnames('d-flex align-items-center justify-content-center',)}
                        variant={'outlined'}
                        color={'primary'}
                        onClick={() => addNewProperties()}
                        disabled={!!loading}
                    >
                        <Add className={'mr-2'}/>
                        <p>
                            {
                                loading
                                    ? "Loading..."
                                    : "Add a new property"
                            }
                        </p>
                    </Button>
                </div>
                <Grid container spacing={3} sx={{paddingInline: 2}}>
                    {
                        loading &&
                        Array(12).fill(null).map((_, e) => (
                            <Grid key={`loading-${e}`} xs={12} md={6} xl={4} className={'pb-4'}>
                                <div className={'loading normal'}>
                                    <div/>
                                </div>
                            </Grid>
                        ))
                    }
                    {
                        !loading &&
                        visibleProperties?.map(technicalProperty => (
                            <Grid key={technicalProperty.id} xs={12} md={6} xl={4} className={'pb-4'}>
                                <TechnicalPropertyBlock
                                    property={technicalProperty}
                                    removing={removingIds.includes(technicalProperty.id)}
                                    onUpdateValues={updatePropertyValue}
                                    onRemove={removePropertyFromProduct}
                                    onUpdate={updateProperty}
                                />
                            </Grid>
                        ))
                    }
                    {
                        !loading && !properties.length &&
                        <Grid xs={12} className={'p-4 d-flex align-items-center justify-content-center'}>
                            <Typography variant={'body2'} textAlign={'center'}>
                                This product currently has no properties. Be sure to add some using the designated
                                button.
                            </Typography>
                        </Grid>
                    }
                </Grid>
            </div>
            <UpsertProductTechnicalPropertyDialog
                open={updatePropertyDialogState.open}
                property={updatePropertyDialogState.property}
                onUpsert={updatePropertyDialogState.onUpsert}
                onClose={updatePropertyDialogState.onClose}
                industryId={product?.industryId}
            />
            <ManageProductTechnicalPropertiesDialog
                open={addPropertiesDialogState.open}
                productId={addPropertiesDialogState.productId}
                industryId={addPropertiesDialogState.industryId}
                industryTitle={addPropertiesDialogState.industryTitle}
                selectedPropertyIds={addPropertiesDialogState.existingPropertyIds}
                onSubmit={addPropertiesDialogState.onAdd}
                onClose={() => setAddPropertiesDialogState(initialAddPropertiesDialogState)}
            />
        </>
    );
}

const TechnicalPropertyBlock = ({property, removing, onUpdateValues, onRemove, onUpdate}) => {
    const [value, setValue] = useState(property.value);
    const [options, setOptions] = useState(property.selectedOptions ?? []);
    const [updating, setUpdating] = useState(false);
    const isMounted = useIsMounted();
    const dispatch = useDispatch();

    /**
     * Syncs the selected options of the property with the selected options in the state.
     */
    useEffect(() => {
        setOptions(property.selectedOptions);
    }, [property.selectedOptions]);

    /**
     * Syncs the value of the property with the value in the state.
     */
    useEffect(() => {
        setValue(property.value);
    }, [property.value])

    /**
     * Checks if the value of the property has changed.
     * @type {(function(*, *): (boolean|boolean))}
     */
    const isValueChanged = useCallback((property, {value, options}) => {
        if (property.hasPredefinedValues) {
            const _options = options.map(e => e.optionId) ?? [];
            _options.sort();
            const _selectedOptions = property.selectedOptions?.map(e => e.optionId) ?? [];
            _selectedOptions.sort();
            return !deepEqual(_options, _selectedOptions);
        }
        switch (property.typeId) {
            case ProductTechnicalPropertyTypeIds.date: {
                const valueDate = typeof value === 'object';
                if (valueDate) {
                    return value.isValid() && !value.isSame(property.value, 'day');
                }
                break;
            }
            case ProductTechnicalPropertyTypeIds.boolean: {
                return value !== property.value;
            }
            case ProductTechnicalPropertyTypeIds.fraction: {
                const val = Utils.technicalPropertyFractionToDecimal(value);
                return val?.toString()?.trim() !== property.value?.toString()?.trim() || value?.toString()?.trim() !== property.value?.toString()?.trim();
            }
            default:
                break;
        }
        return value?.toString()?.trim() !== property.value?.toString()?.trim();
    }, [])

    /**
     * Updates the value of the property.
     * @type {(function(*, boolean=): void)}
     */
    const updateValue = useCallback(async (newValue, commit = false) => {
        setValue(newValue);
        if (commit && isValueChanged(property, {value: newValue})) {
            setUpdating(true);
            await onUpdateValues(property.id, {value: newValue, options: []});
            if (!isMounted())
                return;
            setUpdating(false);
        }
    }, [isValueChanged, property, onUpdateValues, isMounted, setValue])

    /**
     * Updates the selected options of the property.
     * @type {(function(*, boolean=): void)}
     */
    const updateOption = useCallback(async (newOptions, commit = true) => {
        setOptions(newOptions);
        if (commit && isValueChanged(property, {options: newOptions})) {
            setUpdating(true);
            await onUpdateValues(property.id, {value: null, options: newOptions});
            if (!isMounted())
                return;
            setUpdating(false);
        }
    }, [isValueChanged, onUpdateValues, property, isMounted])


    /**
     * Reverts the value of the property.
     * @type {(function(): void)}
     */
    const revertValue = useCallback(() => {
        setValue(property.value);
        setOptions(property.selectedOptions ?? []);
    }, [property.selectedOptions, property.value])

    /**
     * Commits the value of the property.
     * @type {(function(): void)}
     */
    const commitValue = useCallback(() => {
        if (property.hasPredefinedValues) {
            void updateOption(options, true);
        } else {
            void updateValue(value, true);
        }
    }, [options, property.hasPredefinedValues, updateOption, updateValue, value])

    /**
     * Fetches the user confirmation before removing the property.
     * @type {(function(): void)}
     */
    const getUserConfirmation = useCallback(() => {
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.removeConfirmation, {
            open: true,
            data: null,
            removedEntryTitle: `${property.title}`,
            onClose: async (confirmed) => {
                if (!confirmed)
                    return;
                return onRemove(property.id);
            }
        }));
    }, [dispatch, onRemove, property.id, property.title])

    const valueChanged = useMemo(() => isValueChanged(property, {value, options}), [isValueChanged, options, property, value]);

    return (
        <Box className={'w-100 d-flex flex-column'} sx={{'.hide-button': {display: 'none !important',}}}>
            <div className={'d-flex align-items-center justify-content-between mb-2'}>
                <Typography variant={'body2'} fontWeight={600}>
                    {property.title ?? ""}
                </Typography>
                <div className={'d-flex align-items-center justify-content-end'}>
                    {
                        valueChanged &&
                        <SaveOrRollback
                            show={!removing}
                            loading={updating}
                            column={false}
                            onRollback={revertValue}
                            onSave={commitValue}
                        />
                    }
                    {
                        !valueChanged &&
                        <>
                            {
                                !property.isDefault &&
                                <IconButton
                                    onPressed={() => onUpdate(property)}
                                    disabled={removing}
                                    color={'paper.primary'}
                                >
                                    <EditOutlined/>
                                </IconButton>
                            }
                            <IconButton
                                disabled={removing}
                                onPressed={getUserConfirmation}
                                color={'paper.error'}
                            >
                                {
                                    removing
                                        ? <CircularProgress size={15} color={'secondary'}/>
                                        : <DeleteOutlined/>
                                }
                            </IconButton>
                        </>
                    }
                </div>
            </div>
            <TechnicalPropertyBlockInput
                property={property}
                value={value}
                onUpdateValue={updateValue}
                onUpdateOptions={updateOption}
                disabled={removing || updating}
                selectedOptions={options}
            />
        </Box>
    )
}


const TechnicalPropertyBlockInput = ({property, value, selectedOptions, onUpdateValue, onUpdateOptions, disabled}) => {
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [getOptions, setGetOptions] = useState(false);
    const [keywords, setKeywords] = useState('');
    const [multiSelectValue, setMultiSelectValue] = useState('');
    const [validation, setValidation] = useState({valid: true, message: ''});
    const isMounted = useIsMounted();
    const gridRef = useRef(null);

    /**
     * Syncs the selected options of the property with the selected options in the state.
     */
    useEffect(() => {
        if (!property.hasPredefinedValues) {
            switch (property.typeId) {
                case ProductTechnicalPropertyTypeIds.fraction: {
                    const val = value;
                    if (Number(val)?.toString() === val?.toString()) {
                        setKeywords(Utils.technicalPropertyDecimalToFraction(val));
                        break;
                    }
                    const normalized = Utils.normalizeIndustryPropertyPredefinedOptionContent(
                        property.typeId,
                        val?.toString?.() ?? '',
                    );
                    setKeywords(
                        normalized.valid
                            ? val
                            : (Utils.technicalPropertyDecimalToFraction(val) ?? ''),
                    );
                    break;
                }
                default:
                    setKeywords(value ?? "");
                    break;
            }
        } else {
            setKeywords('')
        }
    }, [property.hasPredefinedValues, value, property.typeId])

    /**
     * Fetches the suggested options from the api.
     * @type {(function(*, *): Promise<void>)}
     */
    const fetchSuggestedOptions = useCallback(async (keywords, cancelToken) => {
        if (!isMounted())
            return;
        if (!keywords) {
            setLoading(false);
            setOptions([]);
            return;
        }
        setLoading(true);
        const forApi = {
            industryPropertyId: property.id,
            keyword: keywords
        }
        const response = await Api.autoSuggestIndustryPropertyValue(forApi, cancelToken);
        if (!isMounted())
            return;
        if (!response?.aborted) {
            setLoading(false);
        }
        if (response?.resultFlag) {
            setOptions([...new Set(response.data.map(e => e.value))].map(e => ({title: e, id: createUUId(true),})));
        }
    }, [isMounted, property.id])

    /**
     * With each change in the keywords:
     * - fetches the suggested options.
     */
    useEffect(() => {
        if (property.hasPredefinedValues || (
            property.typeId === ProductTechnicalPropertyTypeIds.date ||
            property.typeId === ProductTechnicalPropertyTypeIds.boolean
        )) {
            return;
        }
        setOptions([]);
        const cancelTokenSource = axios.CancelToken.source();
        setTimeout(() => void fetchSuggestedOptions(keywords, cancelTokenSource.token), 1000);
        return () => cancelTokenSource.cancel();
    }, [fetchSuggestedOptions, keywords, property.hasPredefinedValues, property.typeId])

    /**
     * Fetches the options from the api.
     */
    const fetchOptions = useCallback(async (cancelToken) => {
        setLoading(true);
        const response = await Api.listIndustryPropertyPropertyValues(property.id, cancelToken);
        if (!isMounted())
            return;
        if (!response?.aborted) {
            setLoading(false);
        }
        if (response?.resultFlag) {
            const uniqueOptionKeys = [...new Set(response.data?.map(e => e.id) ?? []).values()];
            setOptions(uniqueOptionKeys.map((key) => response.data?.find(e => e.id === key)).map(e => ({
                id: e.id,
                title: e.content,
            })));
        }
    }, [isMounted, property.id]);

    /**
     * With each change in the [useApi] prop: (basically as soon as the component is mounted and the column has an api url)
     * - fetches the options.
     */
    useEffect(() => {
        if (!property.hasPredefinedValues || !getOptions || (
            property.typeId === ProductTechnicalPropertyTypeIds.date ||
            property.typeId === ProductTechnicalPropertyTypeIds.boolean
        ))
            return;
        const cancelTokenSource = axios.CancelToken.source();
        void fetchOptions(cancelTokenSource.token)
        return () => cancelTokenSource.cancel();
    }, [fetchOptions, property.hasPredefinedValues, getOptions, property.typeId]);

    const filterOptions = useMemo(() => createFilterOptions({
        ignoreCase: true,
        limit: 100,
        stringify: (option) => typeof option === "string" ? option : option.title ?? ""
    }), []);


    /**
     * Sets the selected option in the state.
     * @type {(function(*, *, *): void)|*}
     */
    const onChange = useCallback((event, value) => {
        if (!validation.valid && typeof value !== 'object') {
            return;
        }
        if (!property.hasPredefinedValues) {
            const selectedOption = options.find(e => e.id === value?.id);
            onUpdateValue(typeof value !== 'object' ? value : selectedOption?.title);
        } else if (!property.isMultiSelect) {
            const selectedOption = options.find(e => e.id === value?.id);
            onUpdateOptions([{
                title: selectedOption.title,
                optionId: selectedOption.id,
                id: property.partIndustryPropertyId,
            }]);
        } else {
            const selectedOption = options.find(e => e.id === value?.id);
            if (selectedOption && !selectedOptions.some(e => e.optionId === selectedOption.id)) {
                onUpdateOptions([...selectedOptions, {
                    title: selectedOption.title,
                    optionId: selectedOption.id,
                    id: null,
                }]);
            }
            setMultiSelectValue(prevState => prevState === '' ? ' ' : '');
        }
    }, [onUpdateOptions, onUpdateValue, options, property.hasPredefinedValues, property.isMultiSelect, selectedOptions, validation.valid, property.partIndustryPropertyId])

    const inputMode = useMemo(() => {
        switch (property.typeId) {
            case ProductTechnicalPropertyTypeIds.number:
            case ProductTechnicalPropertyTypeIds.fraction:
                return 'numeric';
            case ProductTechnicalPropertyTypeIds.decimal:
            case ProductTechnicalPropertyTypeIds.money:
                return 'decimal';
            default:
                return 'text';
        }
    }, [property.typeId])

    const inputType = useMemo(() => {
        switch (property.typeId) {
            case ProductTechnicalPropertyTypeIds.number:
            case ProductTechnicalPropertyTypeIds.decimal:
            case ProductTechnicalPropertyTypeIds.money:
                return 'number';
            default:
                return 'text';
        }
    }, [property.typeId])

    /**
     * With each change in the keywords:
     * - validates the keywords.
     */
    useEffect(() => {
        if (!keywords?.length) {
            setValidation({valid: true, message: ""})
            return;
        }
        const normalized = Utils.normalizeIndustryPropertyPredefinedOptionContent(property.typeId, keywords);
        if (!normalized.valid) {
            setValidation({valid: false, message: normalized.message});
            return;
        }
        setValidation({valid: true, message: ""});
    }, [keywords, property.typeId]);

    /**
     * With each change in the selected options:
     * - validates the selected options.
     */
    useEffect(() => {
        const element = gridRef.current;
        if (!element)
            return;
        const saveButton = element.parentElement.querySelector('.icon-container.save-container');
        if (!saveButton)
            return;
        saveButton?.classList?.toggle?.('hide-button', !validation.valid);
    }, [validation.valid, keywords, value]);

    return (
        <>
            <Grid container spacing={1} ref={gridRef}>
                {
                    property.isMultiSelect &&
                    selectedOptions?.map(option => (
                        <Grid key={option.optionId} xs={'auto'}>
                            <Chips
                                type={ChipsTypes.remove}
                                disabled={disabled}
                                iconPosition={ChipsIconPosition.start}
                                content={option.title}
                                onIconClick={() => onUpdateOptions(selectedOptions.filter(e => e.optionId !== option.optionId))}
                            />
                        </Grid>
                    ))
                }
                <Grid xs={property.isMultiSelect ? 'auto' : 12}>
                    {
                        property.typeId === ProductTechnicalPropertyTypeIds.boolean &&
                        <ToggleButtons
                            size={'medium'}
                            value={value ?? false}
                            onChange={(_, v) => onUpdateValue(v, true)}
                            disabled={disabled}
                            fullWidth
                            options={[
                                {name: "Yes", disabled: disabled, value: 'true',},
                                {name: "No", disabled: disabled, value: 'false',},
                            ]}
                        />
                    }
                    {
                        property.typeId === ProductTechnicalPropertyTypeIds.date &&
                        <DatePicker
                            onChange={(v) => onUpdateValue(v)}
                            value={value ? DateUtils.serverToUTC(value) : null}
                            disabled={disabled}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                }
                            }}
                        />
                    }
                    {
                        property.typeId === ProductTechnicalPropertyTypeIds.color &&
                        <ColorTextField
                            value={value}
                            onChange={(v) => onUpdateValue(v, true)}
                            disabled={disabled}
                        />
                    }
                    {
                        property.typeId !== ProductTechnicalPropertyTypeIds.boolean &&
                        property.typeId !== ProductTechnicalPropertyTypeIds.date &&
                        property.typeId !== ProductTechnicalPropertyTypeIds.color &&
                        <Autocomplete
                            fullWidth={!property.isMultiSelect}
                            onFocus={() => setGetOptions(true)}
                            options={options}
                            disabled={disabled}
                            readOnly={disabled}
                            filterOptions={filterOptions}
                            onChange={onChange}
                            size={property.isMultiSelect ? 'small' : 'medium'}
                            getOptionLabel={(option) => {
                                const label = option?.title ?? option?.toString() ?? "";
                                if (property.typeId === ProductTechnicalPropertyTypeIds.fraction) {
                                    if (Number(label)?.toString() === label?.toString()) {
                                        return Utils.technicalPropertyDecimalToFraction(label)
                                    }
                                    const normalized = Utils.normalizeIndustryPropertyPredefinedOptionContent(
                                        property.typeId,
                                        label,
                                    );
                                    if (normalized.valid) {
                                        return label;
                                    }
                                    return Utils.technicalPropertyDecimalToFraction(label) ?? "";
                                }
                                return label
                            }}
                            getOptionKey={(option) => option?.id ?? ""}
                            inputMode={inputMode}
                            componentsProps={{
                                clearIndicator: {color: 'default'},
                                popupIndicator: {color: 'default'},
                            }}
                            renderInput={(params) =>
                                <TextField
                                    {...params}
                                    helperText={validation.message}
                                    {...(property.typeId === ProductTechnicalPropertyTypeIds.fraction && !validation.message && !property.hasPredefinedValues) && {
                                        helperText: "Enter the fraction in the format of '2' or '1/2' or '2-1/2'",
                                    }}
                                    error={!validation.valid}
                                    type={inputType}
                                    size={property.isMultiSelect ? 'small' : 'medium'}
                                    {...(property.isMultiSelect && {
                                        label: 'New Option',
                                    })}
                                    {...(!property.hasPredefinedValues && {
                                        onBlur: (e) => onUpdateValue(!e.target.value ? null : e.target.value, true),
                                        onKeyDown: (e) => {
                                            if (e.key === 'Enter') {
                                                onUpdateValue(!e.target.value ? null : e.target.value, true)
                                            }
                                        }
                                    })}
                                    InputProps={{
                                        ...params.InputProps,
                                        ...(property.typeId === ProductTechnicalPropertyTypeIds.money && {
                                            startAdornment: (
                                                <Typography variant={'body2'} fontWeight={'bold'} color={'primary'}>
                                                    $
                                                </Typography>
                                            )
                                        }),
                                        endAdornment: (
                                            <React.Fragment>
                                                {
                                                    !!property.hasPredefinedValues &&
                                                    (loading ? <CircularProgress color="inherit" size={20}/> : null)
                                                }
                                                {params.InputProps.endAdornment}
                                            </React.Fragment>
                                        ),
                                    }}
                                />
                            }
                            {...(!property.hasPredefinedValues && {
                                value: value ?? "",
                                freeSolo: true,
                                options: options.map(e => e.title),
                                onInputChange: (event, value) => setKeywords(value),
                                inputValue: keywords ?? "",
                                isOptionEqualToValue: (option, value) => option?.trim() === value?.trim(),
                                renderOption: (props, option, state) => {
                                    const matches = match(option, state.inputValue);
                                    const characters = parse(option, matches);
                                    return (
                                        <Box {...props} key={props.key}>
                                            <Typography variant={'body2'}>
                                                {
                                                    characters.map((character, index) => (
                                                        <span
                                                            key={index}
                                                            style={{fontWeight: character.highlight ? 600 : 400}}>
                                                        {character.text}
                                                    </span>
                                                    ))
                                                }
                                            </Typography>
                                        </Box>
                                    );
                                },
                            })}
                            {...(property.hasPredefinedValues && {
                                value: selectedOptions?.[0] ?? "",
                                inputValue: undefined,
                                options: options,
                                blurOnSelect: true,
                                disableClearable: true,
                                isOptionEqualToValue: (option, value) => option.id === value?.optionId,
                                ...(property.isMultiSelect && {
                                    filterOptions: (options, state) => {
                                        const selectedIds = selectedOptions.map(e => e.optionId);
                                        const filtered = filterOptions(options, state);
                                        return filtered.filter(e => !selectedIds.includes(e.id))
                                    },
                                    disabled: false,
                                    filterSelectedOptions: true,
                                    value: multiSelectValue,
                                    disableCloseOnSelect: true,
                                    inputValue: undefined,
                                    sx: {minWidth: 180},
                                    clearOnBlur: true,
                                    blurOnSelect: false,
                                }),
                            })}
                        />
                    }
                </Grid>
            </Grid>
        </>
    );
}

export default ProductTechnicalPropertiesSection;

